import React, { FC } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { SettingFormData } from "./validation.schema";
import { SettingTypeEnum, SettingApplicationEnum } from "@/domain/setting.enum";

export interface SettingModalPresenterProps {
  open: boolean;
  mode: "create" | "edit";
  isSubmitting: boolean;
  isLoadingSettingData: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

export const SettingModalPresenter: FC<SettingModalPresenterProps> = ({
  open,
  mode,
  isSubmitting,
  isLoadingSettingData,
  onClose,
  onSubmit,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useFormContext<SettingFormData>();

  const title = mode === "create" ? "Create Setting" : "Edit Setting";
  const selectedType = watch("type");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="headlineSmall">{title}</Typography>
      </DialogTitle>

      <DialogContent dividers>
        {isLoadingSettingData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: 1,
            }}
          >
            {/* Product */}
            <Controller
              name="product"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Product"
                  fullWidth
                  error={!!errors.product}
                  helperText={errors.product?.message}
                  required
                />
              )}
            />

            {/* Application */}
            <Controller
              name="application"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Application"
                  fullWidth
                  error={!!errors.application}
                  helperText={errors.application?.message}
                  required
                >
                  {Object.values(SettingApplicationEnum).map((app) => (
                    <MenuItem key={app} value={app}>
                      {app.charAt(0).toUpperCase() + app.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/* Module */}
            <Controller
              name="module"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Module"
                  fullWidth
                  error={!!errors.module}
                  helperText={errors.module?.message}
                  required
                />
              )}
            />

            {/* Section */}
            <Controller
              name="section"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Section"
                  fullWidth
                  error={!!errors.section}
                  helperText={errors.section?.message}
                  required
                />
              )}
            />

            {/* Name */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  required
                />
              )}
            />

            {/* Description */}
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  required
                />
              )}
            />

            {/* Type */}
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Type"
                  fullWidth
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  required
                >
                  {Object.values(SettingTypeEnum).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/* Default Value - render differently based on type */}
            <Controller
              name="default"
              control={control}
              render={({ field }) => {
                if (selectedType === SettingTypeEnum.BOOLEAN) {
                  return (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            field.value === true || field.value === "true"
                          }
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Default Value"
                    />
                  );
                } else if (selectedType === SettingTypeEnum.NUMBER) {
                  return (
                    <TextField
                      {...field}
                      label="Default Value"
                      type="number"
                      fullWidth
                      error={!!errors.default}
                      helperText={errors.default?.message}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      required
                    />
                  );
                } else if (selectedType === SettingTypeEnum.JSON) {
                  return (
                    <TextField
                      {...field}
                      label="Default Value (JSON)"
                      fullWidth
                      multiline
                      rows={4}
                      error={!!errors.default}
                      helperText={errors.default?.message}
                      required
                    />
                  );
                } else if (selectedType === SettingTypeEnum.ARRAY) {
                  return (
                    <TextField
                      {...field}
                      label='Default Value (Array - e.g., ["item1", "item2"])'
                      fullWidth
                      multiline
                      rows={4}
                      error={!!errors.default}
                      helperText={errors.default?.message}
                      required
                    />
                  );
                }
                // Default to STRING
                return (
                  <TextField
                    {...field}
                    label="Default Value"
                    fullWidth
                    error={!!errors.default}
                    helperText={errors.default?.message}
                    required
                  />
                );
              }}
            />

            {/* Value - render differently based on type */}
            <Controller
              name="value"
              control={control}
              render={({ field }) => {
                if (selectedType === SettingTypeEnum.BOOLEAN) {
                  return (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            field.value === true || field.value === "true"
                          }
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Value"
                    />
                  );
                } else if (selectedType === SettingTypeEnum.NUMBER) {
                  return (
                    <TextField
                      {...field}
                      label="Value"
                      type="number"
                      fullWidth
                      error={!!errors.value}
                      helperText={errors.value?.message}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
                    />
                  );
                } else if (selectedType === SettingTypeEnum.JSON) {
                  return (
                    <TextField
                      {...field}
                      label="Value (JSON)"
                      fullWidth
                      multiline
                      rows={4}
                      error={!!errors.value}
                      helperText={errors.value?.message}
                    />
                  );
                } else if (selectedType === SettingTypeEnum.ARRAY) {
                  return (
                    <TextField
                      {...field}
                      label='Value (Array - e.g., ["item1", "item2"])'
                      fullWidth
                      multiline
                      rows={4}
                      error={!!errors.value}
                      helperText={errors.value?.message}
                    />
                  );
                }
                // Default to STRING
                return (
                  <TextField
                    {...field}
                    label="Value"
                    fullWidth
                    error={!!errors.value}
                    helperText={errors.value?.message}
                  />
                );
              }}
            />

            {/* Active */}
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Active"
                />
              )}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "flex-end",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={isSubmitting || isLoadingSettingData}
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : mode === "create" ? (
              "Create"
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
